'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { useParams } from 'next/navigation';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const messages = {
  en: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again or return to the home page.',
    retry: 'Try again',
    home: 'Go home',
  },
  fr: {
    title: 'Une erreur est survenue',
    description: 'Une erreur inattendue s\u2019est produite. Veuillez réessayer ou retourner à la page d\u2019accueil.',
    retry: 'Réessayer',
    home: 'Accueil',
  },
  es: {
    title: 'Algo salió mal',
    description: 'Ocurrió un error inesperado. Por favor, inténtelo de nuevo o vuelva a la página de inicio.',
    retry: 'Intentar de nuevo',
    home: 'Inicio',
  },
} as const;

export default function LocaleError({ error, reset }: ErrorProps) {
  const params = useParams();
  const locale = (params?.locale as string) || 'en';
  const t = messages[locale as keyof typeof messages] ?? messages.en;

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-stone-900 mb-2">
          {t.title}
        </h1>
        <p className="text-stone-500 text-sm mb-8">
          {t.description}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={reset}
            className="px-6 py-2.5 rounded-xl bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors"
          >
            {t.retry}
          </button>
          <a
            href={`/${locale}`}
            className="px-6 py-2.5 rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-100 transition-colors"
          >
            {t.home}
          </a>
        </div>
        {process.env.NODE_ENV === 'development' && error.message && (
          <pre className="mt-8 p-4 bg-red-50 border border-red-100 rounded-xl text-left text-xs text-red-800 overflow-auto max-h-40">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );
}
